import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Badge,
  Button,
  Group,
  Stack,
  Progress,
  Loader,
  Alert,
  Table,
} from '@mantine/core';
import { IconArrowLeft, IconCheck, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  useCourse,
  useMyEnrollments,
  useEnroll,
  useUpdateModuleProgress,
  useDeleteCourse,
  useCourseEnrollments,
} from '../../../hooks/useApi';
import { hasGrantOnOthers, useMyPermissions, usePermission } from '../../../hooks/usePermission';
import { modals } from '@mantine/modals';
import { ActionIcon, Tooltip } from '@mantine/core';
import { PageContainer } from '../../../components/ui/PageContainer';
import { ModuleList } from '../../../components/learning/ModuleList';
import { Enrollment } from '../../../types';

export const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Управление курсами — право learning.manage с сервера, а не флаг admin.
  const isLearningManager = usePermission('learning.manage');
  // Кто записан — тем, кто видит обучение других (руководитель, админ): сервер отдаёт только
  // людей в охвате learning.progress.
  const canSeeParticipants = hasGrantOnOthers(useMyPermissions().data, 'learning.progress');
  const { data: participants = [] } = useCourseEnrollments(id || '', canSeeParticipants);

  const { data: course, isLoading: isCourseLoading, error: courseError } = useCourse(id || '');
  const { data: enrollments = [], isLoading: isEnrollmentsLoading } = useMyEnrollments();
  const enroll = useEnroll();
  const updateModuleProgress = useUpdateModuleProgress();
  const deleteCourse = useDeleteCourse();

  const enrollment: Enrollment | undefined = React.useMemo(() => {
    return enrollments.find((e) => {
      const courseId = typeof e.courseId === 'string' ? e.courseId : e.courseId._id;
      return courseId === id;
    });
  }, [enrollments, id]);

  const isEnrolled = !!enrollment;

  const isAllModulesCompleted = React.useMemo(() => {
    if (!course || !enrollment) return false;
    if (course.modules.length === 0) return true;
    return course.modules.every((mod) =>
      enrollment.moduleProgress.some((mp) => mp.moduleId === mod._id && mp.isCompleted),
    );
  }, [course, enrollment]);

  const hasQuiz = course && course.quizQuestions.length > 0;
  const isCompleted = enrollment?.status === 'completed';

  const handleEnroll = () => {
    if (!id) return;
    enroll.mutate(id);
  };

  const handleModuleComplete = (moduleId: string, isCompleted: boolean) => {
    if (!enrollment) return;
    updateModuleProgress.mutate({
      enrollmentId: enrollment._id,
      moduleId,
      isCompleted,
    });
  };

  if (isCourseLoading || isEnrollmentsLoading) {
    return (
      <PageContainer>
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      </PageContainer>
    );
  }

  if (courseError || !course) {
    return (
      <PageContainer>
        <Alert color="red" title={t('common.error')}>
          {t('messages.errorLoading', { item: t('learning.title') })}
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Button
            variant="light"
            leftSection={<IconArrowLeft size={16} />}
            size="sm"
            onClick={() => navigate('/learning')}
          >
            {t('common.back')}
          </Button>

          {isLearningManager && (
            <Group gap="xs">
              <Button variant="outline" size="sm" onClick={() => navigate(`/learning/${id}/edit`)}>
                {t('learning.editCourse')}
              </Button>
              <Tooltip label={t('learning.deleteCourse')}>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  onClick={() => {
                    modals.openConfirmModal({
                      title: t('learning.deleteCourse'),
                      children: <Text size="sm">{t('learning.deleteConfirm')}</Text>,
                      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
                      confirmProps: { color: 'red' },
                      onConfirm: async () => {
                        try {
                          await deleteCourse.mutateAsync(id!);
                          navigate('/learning');
                        } catch {
                          // Error handled by mutation
                        }
                      },
                    });
                  }}
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Group>

        <Group gap="sm" align="center">
          <Title order={2}>{course.title}</Title>
          <Badge color={course.type === 'scorm' ? 'violet' : 'teal'} variant="light">
            {course.type === 'scorm' ? t('learning.scormCourse') : t('learning.customCourse')}
          </Badge>
          {course.isPublished ? (
            <Badge color="green" variant="light">
              {t('learning.published')}
            </Badge>
          ) : (
            <Badge color="gray" variant="light">
              {t('learning.draft')}
            </Badge>
          )}
        </Group>

        {course.description && <Text c="dimmed">{course.description}</Text>}

        {course.authorId && (
          <Text size="sm" c="dimmed">
            {course.authorId.name}
          </Text>
        )}

        {isCompleted && enrollment?.completedAt && (
          <Badge color="green" size="lg" leftSection={<IconCheck size={14} />}>
            {t('learning.completedAt')}: {new Date(enrollment.completedAt).toLocaleDateString()}
          </Badge>
        )}

        {isEnrolled && (
          <div>
            <Text size="sm" mb={4}>
              {t('learning.progress')}: {Math.round(enrollment!.progress)}%
            </Text>
            <Progress value={enrollment!.progress} size="md" />
          </div>
        )}

        {course.type === 'scorm' && isEnrolled && (
          <Button onClick={() => navigate(`/learning/${id}/scorm`)}>{t('learning.launch')}</Button>
        )}

        {course.type === 'custom' && isEnrolled && (
          <ModuleList
            courseId={course._id}
            modules={course.modules}
            moduleProgress={enrollment!.moduleProgress}
            onModuleComplete={handleModuleComplete}
            isDisabled={updateModuleProgress.isPending}
          />
        )}

        <Group gap="sm">
          {!isEnrolled && (
            <Button onClick={handleEnroll} loading={enroll.isPending}>
              {t('learning.enroll')}
            </Button>
          )}

          {isEnrolled && hasQuiz && isAllModulesCompleted && !isCompleted && (
            <Button onClick={() => navigate(`/learning/${id}/quiz`)}>
              {t('learning.takeQuiz')}
            </Button>
          )}

          {isEnrolled && hasQuiz && !isAllModulesCompleted && (
            <Text size="sm" c="dimmed">
              {t('learning.allModulesRequired')}
            </Text>
          )}
        </Group>

        {canSeeParticipants && (
          <Stack gap="xs" mt="lg" data-testid="course-participants">
            <Title order={4}>{t('learning.participants')}</Title>
            {participants.length === 0 ? (
              <Text size="sm" c="dimmed">
                {t('learning.participantsEmpty')}
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={480}>
                <Table verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('learning.participant')}</Table.Th>
                      <Table.Th>{t('learning.progress')}</Table.Th>
                      <Table.Th>{t('learning.completedAt')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {participants.map((p) => (
                      <Table.Tr key={p._id}>
                        <Table.Td>
                          <Text
                            size="sm"
                            component="a"
                            href={`/profile/${p.userId._id}/learning`}
                            onClick={(e: React.MouseEvent) => {
                              e.preventDefault();
                              navigate(`/profile/${p.userId._id}/learning`);
                            }}
                          >
                            {p.userId.name}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" color={STATUS_COLOR[p.status]} tt="none" fw={500}>
                            {t(STATUS_LABEL[p.status])}
                            {/* У SCORM прогресс только 0 или 100 — процент ничего не говорит. */}
                            {p.status !== 'completed' &&
                              course.type !== 'scorm' &&
                              ` · ${Math.round(p.progress)}%`}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {p.completedAt ? new Date(p.completedAt).toLocaleDateString() : '—'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Stack>
        )}
      </Stack>
    </PageContainer>
  );
};

const STATUS_LABEL: Record<Enrollment['status'], string> = {
  enrolled: 'learning.statusEnrolled',
  in_progress: 'learning.statusInProgress',
  completed: 'learning.statusCompleted',
  failed: 'learning.failed',
};
const STATUS_COLOR: Record<Enrollment['status'], string> = {
  enrolled: 'gray',
  in_progress: 'blue',
  completed: 'teal',
  failed: 'red',
};
